<template>
  <div class="relative w-[150px]">
    <img
      v-if="kind === 'photo'"
      :src="url"
      :alt="t('photo')"
      class="aspect-[4/3] w-full rounded-md object-cover"
    />
    <video v-else controls class="max-h-[120px] w-full rounded-md">
      <source :src="url" />
    </video>
    <CVButton
      variant="ghost"
      size="iconXs"
      class="absolute right-1 top-1 bg-white/80 text-red-600"
      :aria-label="t('remove')"
      @click.prevent="emit('remove')"
    >
      <XIcon class="size-4" />
    </CVButton>
    <span
      v-if="status"
      class="absolute bottom-1 right-1 flex size-5 items-center justify-center rounded-full bg-black/60 text-white"
      :aria-label="t(STATUS_DISPLAY[status].messageKey)"
    >
      <component
        :is="STATUS_DISPLAY[status].icon"
        class="size-3"
        :class="STATUS_DISPLAY[status].class"
      />
    </span>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { AlertTriangleIcon, CheckIcon, Loader2Icon, XIcon } from 'lucide-vue-next';
import type { EvidenceUploadStatus } from '@/utils/evidence-upload-queue';
import CVButton from '@/components/buttons/CVButton.vue';

/** A Damage photo or video in the form, with the state of its upload when it is queued. */
defineProps<{
  kind: 'photo' | 'video';
  url: string;
  /** Upload state; omitted when the file only uploads on save. */
  status?: EvidenceUploadStatus;
}>();

const emit = defineEmits<{
  (e: 'remove'): void;
}>();

const { t } = useI18n();

const STATUS_DISPLAY = {
  uploading: { icon: Loader2Icon, class: 'animate-spin', messageKey: 'uploading' },
  done: { icon: CheckIcon, class: '', messageKey: 'uploaded' },
  error: { icon: AlertTriangleIcon, class: 'text-hazel-300', messageKey: 'uploadFailed' },
} as const;
</script>

<i18n lang="json">
{
  "en": {
    "photo": "Damage photo",
    "remove": "Remove",
    "uploading": "Upload running",
    "uploaded": "Upload finished",
    "uploadFailed": "Upload failed, retrying"
  },
  "de": {
    "photo": "Schadensfoto",
    "remove": "Entfernen",
    "uploading": "Upload läuft",
    "uploaded": "Upload fertig",
    "uploadFailed": "Upload fehlgeschlagen, wird erneut versucht"
  },
  "es": {
    "photo": "Foto del daño",
    "remove": "Eliminar",
    "uploading": "Subida en curso",
    "uploaded": "Subida terminada",
    "uploadFailed": "Error al subir, se reintenta"
  }
}
</i18n>
